import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  AppButton,
  Box,
  Controller,
  FieldWrapper,
  FormProvider,
  Label,
  Preloader,
  RegularField,
  SelectDropdown,
  Text,
} from '@common-fe/common-fe';

import { Option } from '@/common/types';
import { Modal } from '@/components';

import { COPY_FROM_ID_FIELD_KEY, NAME_FIELD_KEY } from '../CreatePayoutDefinitionModal.constants';
import { CreatePayoutDefinitionFormValues } from '../CreatePayoutDefinitionModal.types';

interface CreatePayoutDefinitionModalProps {
  visible: boolean;
  methods: UseFormReturn<CreatePayoutDefinitionFormValues>;
  payoutDefinitionOptions: Option[];
  isLoading: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

const CreatePayoutDefinitionModal: React.FC<CreatePayoutDefinitionModalProps> = ({
  visible,
  methods,
  payoutDefinitionOptions,
  isLoading,
  onClose,
  onSubmit,
}) => {
  const {
    control,
    watch,
    formState: { errors },
  } = methods;

  const nameValue = watch(NAME_FIELD_KEY);

  return (
    <Modal visible={visible} onSetVisible={onClose}>
      <FormProvider {...methods}>
        <Box direction="column" gap="spacing24">
          <Box direction="row" align="center" justify="center">
            <Text size="2xl" color="textTitle" weight="bold">
              Create Payout Definition
            </Text>
          </Box>
          <Box background="module" round="moduleRound" pad="spacing24">
            <Box
              background="canvas"
              border={{ size: 'small', color: 'border2' }}
              round="container1Round"
              pad="spacing24"
              gap="spacing16"
            >
              <FieldWrapper align="center" style={{ justifyContent: 'space-between' }}>
                <Label showRequireIcon name={NAME_FIELD_KEY} isNarrow>
                  Payout definition name
                </Label>
                <Box width="320px">
                  <Controller
                    name={NAME_FIELD_KEY}
                    control={control}
                    render={({ field }) => (
                      <RegularField
                        {...field}
                        testId="create-payout-definition-modal_name-input"
                        value={field.value ?? ''}
                        placeholder="Enter payout definition name"
                        errorText={errors.name?.message}
                        editMode
                      />
                    )}
                  />
                </Box>
              </FieldWrapper>
              <FieldWrapper align="center" style={{ justifyContent: 'space-between' }}>
                <Label name={COPY_FROM_ID_FIELD_KEY} isNarrow>
                  Copy settings from
                </Label>
                <Box width="320px">
                  <Controller
                    name={COPY_FROM_ID_FIELD_KEY}
                    control={control}
                    render={({ field }) => (
                      <SelectDropdown
                        testId="create-payout-definition-modal_copy-from-dropdown"
                        options={payoutDefinitionOptions}
                        value={field.value}
                        onChange={(value) => field.onChange(value)}
                        placeholder="Select payout definition"
                        singleMode
                        optional
                        id={COPY_FROM_ID_FIELD_KEY}
                        name={COPY_FROM_ID_FIELD_KEY}
                      />
                    )}
                  />
                </Box>
              </FieldWrapper>
            </Box>
          </Box>
          <Box direction="row" align="center" justify="end" gap="s">
            <AppButton
              testId="create-payout-definition-modal_cancel-button"
              buttonType="secondary"
              width="control"
              onClick={onClose}
            >
              Cancel
            </AppButton>
            <AppButton
              testId="create-payout-definition-modal_submit-button"
              width="control"
              onClick={onSubmit}
              disabled={isLoading || !nameValue?.trim()}
            >
              {isLoading ? <Preloader color="white" /> : 'Next'}
            </AppButton>
          </Box>
        </Box>
      </FormProvider>
    </Modal>
  );
};

export default CreatePayoutDefinitionModal;
